import React, { useState } from 'react';
import { BOARD_CARD_FIELDS } from '../../constants/boardCardFields';

// Board layout editor: which stages appear, in what order, and what a card shows.
//
// Everything here is presentation. Hiding a stage doesn't archive its jobs — they
// stay in the pipeline and keep firing automations — so the panel says so rather
// than letting anyone assume otherwise.
const BoardSettings = ({
  columns,          // [{ key, label }] in current display order
  hidden,           // Set of hidden status keys
  cardFields,       // array of field keys, in display order
  onToggleHidden,
  onMoveColumn,
  onToggleField,
  onMoveField,
  onReset,
  onClose,
  saving,
}) => {
  const [tab, setTab] = useState('stages');

  return (
    <div className="board-settings-backdrop" onClick={onClose}>
      <div className="board-settings" onClick={(e) => e.stopPropagation()}>
        <div className="board-settings-head">
          <h3>Board Layout</h3>
          <button className="board-settings-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="board-settings-tabs">
          <button className={tab === 'stages' ? 'active' : ''} onClick={() => setTab('stages')}>Stages</button>
          <button className={tab === 'cards' ? 'active' : ''} onClick={() => setTab('cards')}>Card Fields</button>
        </div>

        {tab === 'stages' ? (
          <>
            <p className="board-settings-note">
              Uncheck a stage to hide its column. Jobs in a hidden stage stay in the
              pipeline and keep running their automations — you just stop seeing the column.
            </p>
            <ul className="board-settings-list">
              {columns.map((col, idx) => (
                <li key={col.key}>
                  <label>
                    <input
                      type="checkbox"
                      checked={!hidden.has(col.key)}
                      onChange={() => onToggleHidden(col.key)}
                    />
                    <span>{col.label}</span>
                  </label>
                  <span className="board-settings-move">
                    <button onClick={() => onMoveColumn(idx, -1)} disabled={idx === 0} aria-label="Move left">↑</button>
                    <button onClick={() => onMoveColumn(idx, 1)} disabled={idx === columns.length - 1} aria-label="Move right">↓</button>
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="board-settings-note">
              Pick what shows on each card. Checked fields appear in the order below;
              a field with no value on a job is left off that card rather than showing blank.
            </p>
            <ul className="board-settings-list">
              {/* Selected fields first, in their display order, then the rest. */}
              {[...cardFields, ...BOARD_CARD_FIELDS.map(f => f.key).filter(k => !cardFields.includes(k))]
                .map((key) => {
                  const field = BOARD_CARD_FIELDS.find(f => f.key === key);
                  if (!field) return null;
                  const selectedIdx = cardFields.indexOf(key);
                  const isSelected = selectedIdx !== -1;
                  return (
                    <li key={key}>
                      <label>
                        <input type="checkbox" checked={isSelected} onChange={() => onToggleField(key)} />
                        <span>{field.label}</span>
                      </label>
                      {isSelected && (
                        <span className="board-settings-move">
                          <button onClick={() => onMoveField(selectedIdx, -1)} disabled={selectedIdx === 0} aria-label="Move up">↑</button>
                          <button
                            onClick={() => onMoveField(selectedIdx, 1)}
                            disabled={selectedIdx === cardFields.length - 1}
                            aria-label="Move down"
                          >↓</button>
                        </span>
                      )}
                    </li>
                  );
                })}
            </ul>
          </>
        )}

        <div className="board-settings-foot">
          <button className="board-settings-reset" onClick={onReset} disabled={saving}>
            Reset to default
          </button>
          <span className="board-settings-status">{saving ? 'Saving…' : 'Saved automatically'}</span>
        </div>
      </div>
    </div>
  );
};

export default BoardSettings;
